import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, Send, Phone, Save, ArrowLeft, Bell, Ticket } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Switch } from "../components/ui/switch";
import RaeinWallet from "../components/RaeinWallet";
import { Coins } from "lucide-react";

const FIELDS = [
  { key: "first_name", label: "Nome", icon: User, placeholder: "Mario" },
  { key: "last_name", label: "Cognome", icon: User, placeholder: "Rossi" },
  { key: "telegram", label: "Username Telegram", icon: Send, placeholder: "@username" },
  { key: "phone", label: "Numero di telefono", icon: Phone, placeholder: "+39 333 1234567" },
];

const CATEGORY_LABEL = { crypto: "Crypto", commodities: "Materie prime", forex: "Forex" };

function NotificationPreferences() {
  const [assets, setAssets] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/notification-preferences")
      .then((r) => {
        setAssets(r.data.assets || []);
        setStrategies(r.data.strategies || []);
      })
      .catch(() => toast.error("Impossibile caricare le preferenze notifiche"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (symbol, enabled) => {
    setAssets((prev) => prev.map((a) => (a.symbol === symbol ? { ...a, enabled } : a)));
    setPending(symbol);
    try {
      await api.put("/notification-preferences", { prefs: { [symbol]: enabled } });
    } catch (err) {
      setAssets((prev) => prev.map((a) => (a.symbol === symbol ? { ...a, enabled: !enabled } : a)));
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setPending(null);
    }
  };

  const toggleStrategy = async (label, enabled) => {
    setStrategies((prev) => prev.map((s) => (s.label === label ? { ...s, enabled } : s)));
    setPending(label);
    try {
      await api.put("/notification-preferences", { strategy_prefs: { [label]: enabled } });
    } catch (err) {
      setStrategies((prev) => prev.map((s) => (s.label === label ? { ...s, enabled: !enabled } : s)));
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setPending(null);
    }
  };

  const groups = assets.reduce((acc, a) => {
    (acc[a.category] = acc[a.category] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="bg-[#141414] border border-white/10 rounded-md p-8 mt-6" data-testid="notif-prefs-section">
      <div className="flex items-center gap-2 mb-1.5">
        <Bell size={18} className="text-emerald-400" />
        <h2 className="text-lg font-semibold text-white">Notifiche push</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-6">
        Scegli su quali strategie e asset vuoi ricevere le notifiche push dei nuovi segnali. Le
        notifiche in-app restano sempre disponibili.
      </p>
      {loading ? (
        <p className="text-sm text-zinc-500">Caricamento…</p>
      ) : (
        <div className="space-y-6">
          <div data-testid="notif-strategies-block">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
              Strategie
            </div>
            <div className="space-y-1">
              {strategies.map((s) => (
                <div
                  key={s.label}
                  data-testid={`notif-strategy-row-${s.label}`}
                  className="flex items-center justify-between py-2.5 px-3 rounded-md bg-black/30 border border-white/5"
                >
                  <div className="font-mono text-sm text-white">{s.label}</div>
                  <Switch
                    data-testid={`notif-strategy-toggle-${s.label}`}
                    checked={s.enabled}
                    disabled={pending === s.label}
                    onCheckedChange={(v) => toggleStrategy(s.label, v)}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 -mb-2">
            Asset
          </div>
          {Object.entries(groups).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
                {CATEGORY_LABEL[cat] || cat}
              </div>
              <div className="space-y-1">
                {list.map((a) => (
                  <div
                    key={a.symbol}
                    data-testid={`notif-asset-row-${a.symbol}`}
                    className="flex items-center justify-between py-2.5 px-3 rounded-md bg-black/30 border border-white/5"
                  >
                    <div>
                      <div className="font-mono text-sm text-white">{a.symbol}</div>
                      <div className="text-xs text-zinc-500">{a.name}</div>
                    </div>
                    <Switch
                      data-testid={`notif-toggle-${a.symbol}`}
                      checked={a.enabled}
                      disabled={pending === a.symbol}
                      onCheckedChange={(v) => toggle(a.symbol, v)}
                      className="data-[state=checked]:bg-emerald-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", telegram: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        telegram: user.telegram || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="profile-page">
      <button
        data-testid="profile-back-btn"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6"
      >
        <ArrowLeft size={16} /> Indietro
      </button>
      <div className="mb-8">
        <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20 mb-4">
          <User size={12} /> PROFILO
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Il tuo profilo</h1>
        <p className="text-zinc-400 mt-2">Gestisci i tuoi dati personali e i contatti.</p>
      </div>

      <div className="bg-[#141414] border border-white/10 rounded-md p-8 space-y-6">
        <div>
          <label className="text-xs text-zinc-500 flex items-center gap-1.5 mb-1.5">
            <Mail size={13} /> Email
          </label>
          <input
            data-testid="profile-email"
            value={user?.email || ""}
            disabled
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-zinc-500 cursor-not-allowed"
          />
          <p className="text-[11px] text-zinc-600 mt-1">L'email di accesso non è modificabile.</p>
        </div>

        <div data-testid="profile-lottery">
          <label className="text-xs text-zinc-500 flex items-center gap-1.5 mb-1.5">
            <Ticket size={13} className="text-amber-400" /> Numero fortunato (Lotteria)
          </label>
          <div className="w-full bg-black/40 border border-amber-500/20 rounded-md px-3 py-2.5 flex items-center justify-between">
            <span className="font-mono text-xl font-bold text-amber-400" data-testid="profile-lottery-number">
              {user?.lottery_number ?? "—"}
            </span>
            <span className="text-[11px] text-zinc-500">univoco e permanente</span>
          </div>
          <p className="text-[11px] text-zinc-600 mt-1">Con questo numero partecipi all'estrazione premi (solo abbonati VIP attivi).</p>
        </div>

        {FIELDS.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.key}>
              <label className="text-xs text-zinc-500 flex items-center gap-1.5 mb-1.5">
                <Icon size={13} /> {f.label}
              </label>
              <input
                data-testid={`profile-${f.key}`}
                value={form[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-blue-500/50 focus:outline-none transition-colors"
              />
            </div>
          );
        })}

        <button
          data-testid="profile-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-blue-500 text-white text-sm font-semibold hover:bg-blue-400 transition-colors disabled:opacity-50"
        >
          <Save size={16} /> {saving ? "Salvataggio..." : "Salva modifiche"}
        </button>
      </div>

      {user?.role === "admin" && (
      <div className="bg-[#141414] border border-white/10 rounded-md p-8 mt-6" data-testid="profile-wallet-section">
        <div className="flex items-center gap-2 mb-1.5">
          <Coins size={18} className="text-amber-400" />
          <h2 className="text-lg font-semibold text-white">Il tuo Wallet RAEIN</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-6">
          Collega il tuo wallet crypto (MetaMask, Trust Wallet…) per vedere il saldo RAEIN e comprare il token.
        </p>
        <RaeinWallet />
      </div>
      )}

      <NotificationPreferences />
    </div>
  );
}
